import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaUserPlus, FaCheck, FaTimes } from 'react-icons/fa';
import { Page, PageHeader, Card, Badge, Modal, Alert, Loading, formatDate, formatMoney, initials } from '../../ui';
import { ServiceSummary, CustomerCard, Findings, assignmentStatus } from '../../ui/JobParts';
import { api } from '../../ui/api';
import { getAccount } from '../../session';

/** One audit or booking, seen by the vendor it is assigned to. */
const WorkDetail = ({ kind, idKey, listPath, listLabel }) => {
  const params = useParams();
  const id = params.audit_id || params.booking_id;
  const vendor = getAccount();
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [team, setTeam] = useState(null);
  const [picking, setPicking] = useState(false);
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickError, setPickError] = useState('');

  const load = useCallback(() => {
    api(kind === 'audit' ? `/audit/get_audit/${id}` : `/booking/get_booking/${id}`)
      .then(setJob).catch((e) => setError(e.message));
  }, [kind, id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!picking || team) return;
    api(`/staff/get_staff_by_vendor/${vendor.id}`)
      .then((data) => setTeam(data.filter((m) => m.is_active)))
      .catch((e) => { setPickError(e.message); setTeam([]); });
  }, [picking, team, vendor.id]);

  if (error && !job) return <Page><Alert>{error}</Alert></Page>;
  if (!job) return <Page><Loading /></Page>;

  const assignments = job.vendor_assignment_list || [];
  const latest = assignments.length ? assignments[assignments.length - 1] : null;
  const isMine = latest && latest.vendor && latest.vendor.id === vendor.id;
  if (!isMine) {
    return (
      <Page>
        <PageHeader title={`${kind === 'audit' ? 'Audit' : 'Booking'} ${job[idKey]}`} back={{ to: listPath, label: listLabel }} />
        <Alert tone="info">This job is no longer assigned to you.</Alert>
      </Page>
    );
  }

  const myStatus = assignmentStatus(latest.status_code);
  const staffOnJob = latest.staff_assignment_list || [];
  const canStaff = (latest.status_code === 1 || latest.status_code === 2) && job.status_code >= 0;
  const findings = kind === 'audit'
    ? job.audit_details && job.audit_details.final_audit_details
    : job.audit && job.audit.audit_details && job.audit.audit_details.final_audit_details;
  const price = kind === 'booking' && job.audit && job.audit.final_price ? job.audit.final_price.final_price : null;

  const respond = async (accept) => {
    if (!accept && !window.confirm('Decline this job? It will be offered to another vendor.')) return;
    setBusy(true);
    try {
      await api(`/${kind}/${accept ? 'accept_approval' : 'reject_approval'}/${id}`, { method: 'POST' });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    if (!choice) return;
    setBusy(true);
    setPickError('');
    try {
      await api(`/${kind}/assign_staff/${id}`, { method: 'POST', body: { staff_id: choice.id } });
      setPicking(false);
      load();
    } catch (e) {
      setPickError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <PageHeader
        title={`${kind === 'audit' ? 'Audit' : 'Booking'} ${job[idKey]}`}
        subtitle={`Received ${formatDate(job.date_created)}`}
        back={{ to: listPath, label: listLabel }}
        actions={<Badge tone={myStatus.tone}>{myStatus.label}</Badge>}
      />
      <Alert>{error}</Alert>

      <div className="grid grid-sidebar">
        <div className="stack">
          <ServiceSummary service={job.service} date={job.date} time={job.time} address={job.address} note={job.comment} links={false} />
          <Findings
            title={kind === 'audit' ? 'Survey findings' : 'Agreed scope of work'}
            items={findings}
            onlySelected={kind === 'booking'}
          />
          {job.service.work_orders && job.service.work_orders.length > 0 && (
            <Card title="Work steps">
              <ol className="steps">
                {job.service.work_orders.map((w, i) => <li key={i}>{w.step}</li>)}
              </ol>
            </Card>
          )}
        </div>

        <div className="stack sticky-side side-first">
          {latest.status_code === 0 && (
            <Card title="New request" hint="Accept to take this job, or decline so it can be reassigned.">
              <div className="action-list">
                <button className="btn btn-primary" disabled={busy} onClick={() => respond(true)}><FaCheck /> Accept</button>
                <button className="btn btn-secondary" disabled={busy} onClick={() => respond(false)}><FaTimes /> Decline</button>
              </div>
            </Card>
          )}
          <Card
            title="Technician"
            action={canStaff && (
              <button className="btn btn-primary btn-sm" onClick={() => { setChoice(null); setPicking(true); }}>
                <FaUserPlus /> {staffOnJob.length ? 'Change' : 'Assign'}
              </button>
            )}
          >
            {staffOnJob.length ? (
              <ul className="list">
                {staffOnJob.map((s) => (
                  <li key={s.id}>
                    <div className="avatar">{initials(s.name)}</div>
                    <div style={{ minWidth: 0 }}>
                      <div className="strong">{s.name}</div>
                      <div className="small muted">{s.phone_number}</div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">{canStaff ? 'No one assigned yet.' : 'Accept the job to assign a technician.'}</p>
            )}
          </Card>
          {price !== null && price !== undefined && (
            <Card title="Agreed price"><span className="price">{formatMoney(price)}</span></Card>
          )}
          <CustomerCard user={job.user} links={false} />
        </div>
      </div>

      <Modal
        open={picking}
        title="Assign a technician"
        subtitle="Only verified staff who can sign in are listed."
        onClose={() => setPicking(false)}
        footer={(
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setPicking(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={!choice || busy} onClick={assign}>{busy ? 'Assigning…' : 'Assign'}</button>
          </>
        )}
      >
        <Alert>{pickError}</Alert>
        {!team ? <Loading /> : team.length ? (
          <div className="vendor-pick">
            {team.map((m) => (
              <button key={m.id} type="button" className={`vendor-option${choice && choice.id === m.id ? ' on' : ''}`} onClick={() => setChoice(m)}>
                <div className="avatar">{initials(m.name)}</div>
                <div className="grow">
                  <div className="strong">{m.name}</div>
                  <div className="small muted">{m.phone_number}</div>
                </div>
              </button>
            ))}
          </div>
        ) : <p className="muted">No verified staff yet. Add technicians under Field staff; they can be assigned once Fortune Home Care verifies them.</p>}
      </Modal>
    </Page>
  );
};

export default WorkDetail;
